import {
  type BulletinNote,
  type BulletinSnapshot,
  type InteractionChoice,
  type WhiteboardSnapshot,
  type WhiteboardStroke,
} from '@veriverse/shared';
import { create } from 'zustand';

/** The E menu of an object with several actions (coffee or tea). */
export interface ChoiceMenu {
  readonly objectId: number;
  readonly choices: readonly InteractionChoice[];
}

export interface OpenWhiteboard {
  readonly objectId: number;
  readonly strokes: readonly WhiteboardStroke[];
  readonly canClear: boolean;
}

export interface OpenBulletin {
  readonly objectId: number;
  readonly notes: readonly BulletinNote[];
}

interface BoardState {
  menu: ChoiceMenu | undefined;
  whiteboard: OpenWhiteboard | undefined;
  bulletin: OpenBulletin | undefined;
  /** A board could not be loaded. */
  boardFailed: boolean;

  openMenu(menu: ChoiceMenu | undefined): void;
  openWhiteboard(objectId: number, snapshot: WhiteboardSnapshot): void;
  closeWhiteboard(): void;
  addStroke(objectId: number, stroke: WhiteboardStroke): void;
  eraseStrokes(objectId: number, ids: readonly string[]): void;
  clearWhiteboard(objectId: number): void;
  openBulletin(objectId: number, snapshot: BulletinSnapshot): void;
  closeBulletin(): void;
  addNote(objectId: number, note: BulletinNote): void;
  removeNote(objectId: number, id: string): void;
  setBoardFailed(failed: boolean): void;
}

/**
 * Client state of the drink menu, whiteboard and bulletin board (spec §6.14.3, §6.15). Live
 * events only change a board that is open; opening one always fetches it fresh from the server.
 */
export const useBoardStore = create<BoardState>()((set) => ({
  menu: undefined,
  whiteboard: undefined,
  bulletin: undefined,
  boardFailed: false,

  openMenu: (menu) => set({ menu }),
  openWhiteboard: (objectId, snapshot) =>
    set({
      whiteboard: { objectId, strokes: snapshot.strokes, canClear: snapshot.canClear },
      boardFailed: false,
    }),
  closeWhiteboard: () => set({ whiteboard: undefined }),
  addStroke: (objectId, stroke) =>
    set((state) =>
      state.whiteboard?.objectId === objectId &&
      !state.whiteboard.strokes.some((s) => s.id === stroke.id)
        ? { whiteboard: { ...state.whiteboard, strokes: [...state.whiteboard.strokes, stroke] } }
        : {},
    ),
  eraseStrokes: (objectId, ids) =>
    set((state) => {
      if (state.whiteboard?.objectId !== objectId) return {};
      const gone = new Set(ids);
      return {
        whiteboard: {
          ...state.whiteboard,
          strokes: state.whiteboard.strokes.filter((s) => !gone.has(s.id)),
        },
      };
    }),
  clearWhiteboard: (objectId) =>
    set((state) =>
      state.whiteboard?.objectId === objectId
        ? { whiteboard: { ...state.whiteboard, strokes: [] } }
        : {},
    ),
  openBulletin: (objectId, snapshot) =>
    set({ bulletin: { objectId, notes: snapshot.notes }, boardFailed: false }),
  closeBulletin: () => set({ bulletin: undefined }),
  addNote: (objectId, note) =>
    set((state) =>
      state.bulletin?.objectId === objectId && !state.bulletin.notes.some((n) => n.id === note.id)
        ? { bulletin: { ...state.bulletin, notes: [...state.bulletin.notes, note] } }
        : {},
    ),
  removeNote: (objectId, id) =>
    set((state) =>
      state.bulletin?.objectId === objectId
        ? {
            bulletin: { ...state.bulletin, notes: state.bulletin.notes.filter((n) => n.id !== id) },
          }
        : {},
    ),
  setBoardFailed: (boardFailed) => set({ boardFailed }),
}));
